<script lang="ts">
  import { onMount } from "svelte";
  import { fetchLedColors, patchLedColors, sendLedTest, ApiError } from "../lib/api";
  import { push } from "svelte-spa-router";
  import type { LedColorEntry, LedColorMap } from "@diy-avatar/shared/admin";
  import type { LedStateName } from "@diy-avatar/shared/datachannel";
  import { DEFAULT_LED_COLORS } from "@diy-avatar/shared/datachannel";

  const STATES: LedStateName[] = [
    "ok",
    "student_connected",
    "message",
    "no_internet",
    "raise_hand",
    "audio_on",
    "video_on",
    "hardware_error",
    "off",
  ];

  export let label: string | undefined = undefined;

  let overrides: LedColorMap = {};
  let info: string | null = null;
  let error: string | null = null;
  let saving = false;

  // ---- testmode ----
  const DEMO_SECONDS = 5;
  let manualColor = "FF8800";
  let manualBlink = false;
  let testBusy = false;
  let testInfo: string | null = null;
  let testError: string | null = null;

  async function runTest(
    fn: () => Promise<unknown>,
    okMsg: string,
  ): Promise<void> {
    testBusy = true;
    testInfo = null;
    testError = null;
    try {
      await fn();
      testInfo = okMsg;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      testError = err instanceof Error ? err.message : "test failed";
    } finally {
      testBusy = false;
    }
  }

  function demoState(state: LedStateName) {
    return runTest(
      () => sendLedTest({ mode: "demo", state, duration_s: DEMO_SECONDS }, label),
      `demo "${state}" for ${DEMO_SECONDS}s (queued)`,
    );
  }
  function manualSet() {
    return runTest(
      () =>
        sendLedTest({
          mode: "manual",
          on: true,
          color: manualColor.toUpperCase(),
          blink: manualBlink,
        }, label),
      "LED set (queued)",
    );
  }
  function manualOff() {
    return runTest(
      () => sendLedTest({ mode: "manual", on: false }, label),
      "LED off (queued)",
    );
  }
  function onManualColorPick(e: Event) {
    const v = (e.target as HTMLInputElement).value;
    manualColor = v.replace("#", "").toUpperCase();
  }

  async function load() {
    try {
      const res = await fetchLedColors(label);
      overrides = res.overrides;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "load failed";
    }
  }

  function effective(state: LedStateName): LedColorEntry {
    return overrides[state] ?? DEFAULT_LED_COLORS[state];
  }

  function override(state: LedStateName, color: string, blink: boolean) {
    overrides = { ...overrides, [state]: { color: color.toUpperCase(), blink } };
  }

  function onColorInput(e: Event, state: LedStateName, blink: boolean) {
    const v = (e.target as HTMLInputElement).value;
    override(state, v, blink);
  }
  function onBlinkChange(e: Event, state: LedStateName, color: string) {
    const v = (e.target as HTMLInputElement).checked;
    override(state, color, v);
  }
  function clear(state: LedStateName) {
    const next = { ...overrides };
    delete next[state];
    overrides = next;
  }

  async function save() {
    saving = true;
    info = null;
    error = null;
    try {
      // PATCH expects null to clear, value to set. Build a delta from current state.
      const payload: LedColorMap & Record<string, LedColorEntry | null> = {};
      for (const s of STATES) {
        if (overrides[s] != null) payload[s] = overrides[s]!;
      }
      // For clearing, the UI sets {} for that state — represent via clear() removing key.
      const res = await patchLedColors(payload as LedColorMap, label);
      overrides = res.overrides;
      info = "saved";
    } catch (err) {
      error = err instanceof Error ? err.message : "save failed";
    } finally {
      saving = false;
    }
  }

  onMount(load);
</script>

<section>
  <h1>LED color overrides</h1>
  <p class="hint">
    Effective color = override (if present) else the default from
    <code>DEFAULT_LED_COLORS</code>. Color is RRGGBB hex.
  </p>
  {#if error}
    <p class="error" data-testid="error">{error}</p>
  {/if}
  <table data-testid="led-table">
    <thead>
      <tr>
        <th>state</th>
        <th>color</th>
        <th>blink</th>
        <th>swatch</th>
        <th>actions</th>
        <th>test</th>
      </tr>
    </thead>
    <tbody>
      {#each STATES as state}
        {@const eff = effective(state)}
        {@const has = overrides[state] != null}
        <tr data-testid="led-row-{state}">
          <td>{state}</td>
          <td>
            <input
              type="text"
              value={eff.color}
              maxlength="6"
              pattern={"[0-9A-Fa-f]{6}"}
              data-testid="color-{state}"
              on:input={(e) => onColorInput(e, state, eff.blink)}
            />
          </td>
          <td>
            <input
              type="checkbox"
              checked={eff.blink}
              data-testid="blink-{state}"
              on:change={(e) => onBlinkChange(e, state, eff.color)}
            />
          </td>
          <td><span class="swatch" style="background: #{eff.color};"></span></td>
          <td>
            {#if has}
              <button on:click={() => clear(state)}>reset</button>
            {:else}
              <span class="muted">default</span>
            {/if}
          </td>
          <td>
            <button
              class="test"
              disabled={testBusy}
              data-testid="test-{state}"
              title="Show this state on the device for {DEMO_SECONDS}s"
              on:click={() => demoState(state)}
            >
              Test {DEMO_SECONDS}s
            </button>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
  <button on:click={save} disabled={saving} data-testid="save">
    {saving ? "Saving…" : "Save"}
  </button>
  {#if info}<p class="info" data-testid="info">{info}</p>{/if}

  <h2>Testmode</h2>
  <p class="hint">
    Live-drive the LED to verify the hardware. Commands are picked up on the
    device's next heartbeat (~2s). Per-state <strong>Test {DEMO_SECONDS}s</strong>
    buttons above show that state's <em>saved</em> appearance, then revert.
  </p>
  <div class="testmode" data-testid="testmode">
    <label>
      color
      <input
        type="color"
        value="#{manualColor}"
        data-testid="manual-color-picker"
        on:input={onManualColorPick}
      />
    </label>
    <input
      type="text"
      class="hex"
      bind:value={manualColor}
      maxlength="6"
      pattern={"[0-9A-Fa-f]{6}"}
      data-testid="manual-color"
    />
    <label class="inline">
      <input type="checkbox" bind:checked={manualBlink} data-testid="manual-blink" />
      blink
    </label>
    <button disabled={testBusy} data-testid="manual-set" on:click={manualSet}>
      Set / On
    </button>
    <button disabled={testBusy} data-testid="manual-off" on:click={manualOff}>
      Off
    </button>
  </div>
  {#if testInfo}<p class="info" data-testid="test-info">{testInfo}</p>{/if}
  {#if testError}<p class="error" data-testid="test-error">{testError}</p>{/if}
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  .hint {
    color: #9ca3af;
    font-size: 0.85rem;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
    margin-bottom: 0.75rem;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid #1f2937;
    text-align: left;
    font-size: 0.85rem;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  input[type="text"] {
    width: 8ch;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    padding: 0.3rem;
    font-family: ui-monospace, monospace;
  }
  .swatch {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 1px solid #374151;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  .muted {
    color: #6b7280;
  }
  h2 {
    margin: 1.5rem 0 0.5rem;
  }
  .test {
    background: #0e7490;
    border-color: #155e75;
  }
  .test:disabled {
    opacity: 0.5;
  }
  .testmode {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.75rem;
  }
  .testmode label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #9ca3af;
    font-size: 0.85rem;
  }
  .testmode label.inline {
    gap: 0.3rem;
  }
  .testmode input[type="color"] {
    width: 38px;
    height: 28px;
    padding: 0;
    background: #0f172a;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  .testmode .hex {
    width: 8ch;
  }
  .info {
    color: #6ee7b7;
  }
  .error {
    color: #fca5a5;
  }
</style>
